// src/app/app.module.interceptor.ts

import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { switchMap, map, catchError, filter, take } from 'rxjs/operators';
import { Storage } from '@ionic/storage-angular';
import { AppSettings } from './shared/app-settings';
import { AuthService } from './services/auth.service';

@Injectable()
export class AngularInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private storage: Storage,
    private authService: AuthService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // List of endpoints that don't require authentication
    const publicEndpoints = [
      '/Company/GetCompanies',
      '/auth/refresh',
      '/login',
      '/landax/surveys/kiosk/' // Kiosk mode endpoints
    ];
    
    const isPublicEndpoint = publicEndpoints.some(endpoint => req.url.includes(endpoint));
    
    return from(this.storage.get(AppSettings.StorageKeys.Token)).pipe(
      switchMap(token => {
        let cloned = req;
        
        // Only add auth token if we have one AND it's not a public endpoint
        if (token && !isPublicEndpoint && !req.url.includes('soolo.no')) {
          cloned = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
        } else if (req.url.includes('soolo.no')) {
          cloned = req.clone({ headers: req.headers.set('Authorization', `Bearer ${AppSettings.NorTraceBearer}`) });
        }

        // Only set Content-Type for POST/PUT/PATCH requests with JSON body
        const isFormData = req.body instanceof FormData;
        const hasContentType = req.headers.has('Content-Type');
        const isGetRequest = req.method === 'GET';
        const needsContentType = !isGetRequest && !isFormData && !hasContentType && !req.headers.has('Accept');
        
        if (needsContentType) {
          cloned = cloned.clone({ headers: cloned.headers.set('Content-Type', 'application/json') });
        }

        return next.handle(cloned).pipe(
          catchError((error: HttpErrorResponse) => {
            // Handle 401 Unauthorized - token expired (but not for public endpoints)
            if (error.status === 401 && !isPublicEndpoint) {
              return this.handle401Error(req, next);
            }
            return throwError(error);
          })
        );
      })
    );
  }

  /**
   * Handle 401 Unauthorized by refreshing access token and retrying request
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.authService.refreshAccessToken()).pipe(
        switchMap((newToken: string | null) => {
          this.isRefreshing = false;

          if (newToken) {
            this.refreshTokenSubject.next(newToken);
            // Retry original request with new token
            return this.retryWithNewToken(request, next, newToken);
          } else {
            // Refresh failed - token is invalid/expired
            console.error('Token refresh failed in interceptor, user will be logged out');
            this.refreshTokenSubject.next(null);
            // Return error - authService.logout() already called in refreshAccessToken
            return throwError(() => new Error('Session expired'));
          }
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          console.error('Error during token refresh:', error);
          return throwError(() => error);
        })
      );
    } else {
      // Wait for refresh to complete, then retry with new token
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          if (token && token !== 'REFRESH_FAILED') {
            return this.retryWithNewToken(request, next, token);
          }
          return throwError(() => new Error('Session expired'));
        })
      );
    }
  }

  /**
   * Retry request with new access token
   */
  private retryWithNewToken(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    const cloned = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
    return next.handle(cloned);
  }
}
